import { assign, createMachine } from "xstate";
import {
  fetchConsentsCatalogue,
  fetchUserConsents,
  IConsent,
  IUpdateUserConsent,
  IUpdateUserConsentsPayload,
  updateUserConsents,
} from "@nju/data-access-portal-consents";

export interface IContext {
  consentsCatalogue: IConsent[];
  userConsents: IUpdateUserConsentsPayload;
}

type ISelectConsentEvent = {
  type: "SELECT_CONSENT";
  consent: IUpdateUserConsent;
};

type IEvent =
  | {
      type: "CONSENTS_LOADED";
      consentsCatalogue: IConsent[];
      userConsents: IUpdateUserConsentsPayload;
    }
  | {
      type: "FETCH_CONSENTS_ERROR";
      error: unknown;
    }
  | {
      type: "SUBMIT";
    }
  | {
      type: "UPDATE_CONSENTS_ERROR";
    }
  | {
      type: "UPDATE_CONSENTS_SUCCESS";
    }
  | ISelectConsentEvent;

export const changeConsentsMachine = createMachine(
  {
    schema: {
      context: {
        consentsCatalogue: [],
        userConsents: [],
      } as IContext,
      events: {} as IEvent,
    },
    initial: "init",
    states: {
      init: {
        invoke: {
          src: "fetchData",
        },
        on: {
          CONSENTS_LOADED: [{ actions: ["setData"] }],
          SUBMIT: [{ target: "saving" }],
        },
      },
      saving: {
        invoke: {
          src: "saveConsents",
        },
        on: {
          UPDATE_CONSENTS_SUCCESS: "updateConsentsSuccess",
          UPDATE_CONSENTS_ERROR: "updateConsentsError",
        },
      },
      updateConsentsSuccess: {
        id: "updateConsentsSuccess",
      },
      updateConsentsError: {
        id: "updateConsentsError",
        on: {
          SUBMIT: { target: "saving" },
        },
      },
    },
    on: {
      SELECT_CONSENT: {
        actions: assign<IContext, ISelectConsentEvent>((context, event) => {
          const exists = context.userConsents.some((consent) => {
            return consent.consentId === event.consent.consentId;
          });
          return {
            ...context,
            userConsents: exists
              ? context.userConsents.map((consent) => {
                  return consent.consentId === event.consent.consentId
                    ? { ...consent, accepted: event.consent.accepted }
                    : consent;
                })
              : [
                  ...context.userConsents,
                  {
                    ...event.consent,
                  },
                ],
          };
        }),
      },
    },
  },
  {
    actions: {
      setData: assign<IContext, IEvent>((context, event) => {
        if (event.type === "CONSENTS_LOADED") {
          return {
            consentsCatalogue: event.consentsCatalogue,
            userConsents: event.userConsents.map(
              ({ consentId, consentVersion, accepted }) => {
                return {
                  consentId,
                  consentVersion,
                  accepted,
                };
              },
            ),
          };
        }
        return context;
      }),
    },
    services: {
      fetchData: () => {
        return async (callback) => {
          try {
            const [consentsCatalogue, userConsents] = await Promise.all([
              fetchConsentsCatalogue(),
              fetchUserConsents(),
            ]);
            callback({
              type: "CONSENTS_LOADED",
              consentsCatalogue: consentsCatalogue.unwrap(),
              userConsents: userConsents.unwrap(),
            });
          } catch (error) {
            callback({ type: "FETCH_CONSENTS_ERROR", error });
          }
        };
      },
      saveConsents: (context) => {
        return async (callback) => {
          try {
            await updateUserConsents(context.userConsents);
            callback({ type: "UPDATE_CONSENTS_SUCCESS" });
          } catch {
            callback({ type: "UPDATE_CONSENTS_ERROR" });
          }
        };
      },
    },
  },
);
